import React, { useState, useContext, useRef } from "react";
import { GlobalContext } from "../../../App";
import L from "leaflet";
import { HOST, HOST_MEDIA_URL } from "../../host";
import { logToServer } from "../../logger";
import Modal from "react-modal";

function GeoReference() {
  const [show, setShow] = useState(false);
  const [file, setFile] = useState(null);
  const [mess, setMess] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [overlay, setOverlay] = useState(null);

  const path = useRef(null);
  const toolvisRef = useRef(null);
  const fileInputRef = useRef(null);

  const { map, customMarker, layerControls, getCsrfToken } =
    useContext(GlobalContext);
  const mapRef = useContext(GlobalContext).map;

  const height = useRef(null);
  const width = useRef(null);
  const bounds = useRef(null);
  const CHUNK_SIZE = 50 * 1024 * 1024;

  const [lines, setLines] = useState([]);
  const polylines = useRef({});
  const [markers, setMarkers] = useState([]);
  const [download, setDownload] = useState(false);
  const [modify, setModify] = useState(false);
  const [lineCount, setLineCount] = useState(0);

  const [GeoRefByLine, setGeoRefByLine] = useState(false);
  const [GeoRefByMarker, setGeoRefByMarker] = useState(false);
  const [geoRefMode, setGeoRefMode] = useState("");

  const [inputLatLngModal, setInputLatLngModal] = useState(false);
  const [inputLat, setInputLat] = useState("");
  const [inputLng, setInputLng] = useState("");
  const [initialMarkerLatLng, setInitialMarkerLatLng] = useState(null);

  const rubberLineRef = useRef(null);
  const awaitingGeoEndRef = useRef(false);
  const [awaitingEnd, setAwaitingEnd] = useState(false);
  const activeDrawCleanupRef = useRef(null);

  // ---------------------------------------------------------------------------
  // Validation
  // ---------------------------------------------------------------------------
  const isValidGeoRefImageFile = (name) => {
    const n = (name || "").toLowerCase();
    return n.endsWith(".jpg") || n.endsWith(".png") || n.endsWith(".jpeg");
  };

  // ---------------------------------------------------------------------------
  // Mode toggle
  // ---------------------------------------------------------------------------
  const handleGeoRefModeChange = (mode) => {
    setGeoRefMode(mode);
    if (mode === "marker") {
      setGeoRefByLine(true);
      setGeoRefByMarker(false);
    } else {
      setGeoRefByLine(false);
      setGeoRefByMarker(true);
    }
  };

  // ---------------------------------------------------------------------------
  // File handling
  // ---------------------------------------------------------------------------
  const handleFileChange = (event) => {
    try {
      const selected = Array.from(event.target.files || []);
      const invalid = selected.find((f) => !isValidGeoRefImageFile(f?.name));
      if (invalid) {
        alert("Only .jpg, .png, and .jpeg files are accepted.");
        event.target.value = "";
        setFile(null);
        return;
      }
      setFile(event.target.files);
    } catch {
      alert("Please select File(s)");
    }
  };

  const handleUpload = async () => {
    setMess(null);
    setUploadProgress(0);
    if (!file) { alert("Please select File(s)"); return; }

    try {
      const files = Array.from(file);
      const invalid = files.find((f) => !isValidGeoRefImageFile(f?.name));
      if (invalid) {
        alert("Only .jpg, .png, and .jpeg files are accepted.");
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }

      for (const f of files) {
        const totalChunks = Math.ceil(f.size / CHUNK_SIZE);
        for (let ci = 0; ci < totalChunks; ci++) {
          const start = ci * CHUNK_SIZE;
          const end = Math.min(f.size, start + CHUNK_SIZE);
          const formData = new FormData();
          formData.append("chunk", f.slice(start, end));
          formData.append("chunkIndex", ci);
          formData.append("totalChunks", totalChunks);
          formData.append("fileName", f.name);

          const res = await fetch(`${HOST}/uploads/georef`, {
            method: "POST",
            credentials: "include",
            headers: { "X-CSRFToken": await getCsrfToken() },
            body: formData,
          });

          if (res.ok) {
            const data = await res.json();
            setUploadProgress(Math.round(((ci + 1) / totalChunks) * 100));
            if (ci === totalChunks - 1 && data.path) {
              path.current = data.path;
              width.current = data.width;
              height.current = data.height;
              addImageOverlay(data.path, data.height, data.width, false);
              removeAllLines();
              setUploadProgress(0);
            }
          } else {
            setMess("An error occurred while uploading chunks.");
            return;
          }
        }
      }
    } catch (error) {
      logToServer("error", `Error uploading files: ${error}`);
      setMess("An error occurred while uploading the files.");
      return;
    }

    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ---------------------------------------------------------------------------
  // Image overlay
  // ---------------------------------------------------------------------------
  const addImageOverlay = (imagePath, _h, _w, bound = false) => {
    setDownload(false);
    if (!mapRef) return;

    if (overlay) overlay.remove();

    const imgW = 50, imgH = 50;
    let boundsss;
    if (!bound) {
      boundsss = L.latLngBounds(
        mapRef.containerPointToLatLng([
          (mapRef.getSize().x - imgW) / 2,
          (mapRef.getSize().y - imgH) / 2,
        ]),
        mapRef.containerPointToLatLng([
          (mapRef.getSize().x + imgW) / 2,
          (mapRef.getSize().y + imgH) / 2,
        ])
      );
      bounds.current = boundsss;
    } else {
      boundsss = bounds.current;
    }

    const layerName = `Georeferenced-Image-${Date.now()}`;
    const newOverlay = L.imageOverlay(
      `${HOST_MEDIA_URL}/${imagePath}`,
      boundsss
    ).addTo(mapRef);

    layerControls.addOverlay(
      newOverlay, layerName, false, false, false, layerName, false, null
    );
    setOverlay(newOverlay);
    setGeoRefByLine(false);
    setGeoRefByMarker(false);
    setGeoRefMode("");
    setLines([]);
    setMarkers([]);
    setLineCount(0);
  };

  // ---------------------------------------------------------------------------
  // Rubber-band helpers
  // ---------------------------------------------------------------------------
  const stopRubberBand = (leafletMap) => {
    const m = leafletMap || map || mapRef;
    if (!m) return;
    if (m._georefMoveHandler) {
      m.off("mousemove", m._georefMoveHandler);
      m._georefMoveHandler = null;
    }
    if (rubberLineRef.current) {
      rubberLineRef.current.remove();
      rubberLineRef.current = null;
    }
  };

  // ---------------------------------------------------------------------------
  // HandleDrawClick  (Georeference by Marker mode)
  // ---------------------------------------------------------------------------
  function HandleDrawClick() {
    if (awaitingGeoEndRef.current) return;
    map.getContainer().style.cursor = "crosshair";

    map.once("click", function onGeoStart(e) {
      const startLL = e.latlng;

      if (!bounds.current || !bounds.current.contains(startLL)) {
        alert("The starting point must be inside the image.");
        map.getContainer().style.cursor = "";
        return;
      }

      const startMarker = L.marker(startLL, {
        icon: new customMarker({ type: "geo_start" }),
        draggable: false,
        interactive: false,
      }).addTo(map);
      setMarkers((prev) => [...prev, startMarker]);

      const pixelCoords = latLngToImagePixel(startLL);
      const lineId = Math.random().toString(36).substring(2, 15);

      awaitingGeoEndRef.current = true;
      setAwaitingEnd(true);

      const onMouseMove = (ev) => {
        if (rubberLineRef.current) rubberLineRef.current.remove();
        rubberLineRef.current = L.polyline([startLL, ev.latlng], {
          color: "white",
          dashArray: "8, 10",
          weight: 2,
          opacity: 0.9,
          interactive: false,
        }).addTo(map);
      };

      const onGeoEnd = (ev) => {
        cleanup();
        const endLL = ev.latlng;
        const endMarker = L.marker(endLL, {
          icon: new customMarker({
            type: "geo_end",
            coords: startLL,
            pixel: pixelCoords,
            id: lineId,
          }),
          draggable: true,
        }).addTo(map);

        drawLine(startLL, endLL, pixelCoords, lineId);
        setMarkers((prev) => [...prev, endMarker]);
        setLineCount((prev) => prev + 1);

        endMarker.on("dragend", (dragEv) => {
          const newLL = dragEv.target.getLatLng();
          updateLine(startLL, newLL, lineId);
          setLines((prev) =>
            prev.map((l) =>
              l.pixelCoords === pixelCoords ? { ...l, latLngs: newLL } : l
            )
          );
        });

        logToServer("info", "Georeference line drawn", { lineId });
      };

      const onEscape = (keyEv) => {
        if (keyEv.key === "Escape") {
          cleanup();
          startMarker.remove();
          setMarkers((prev) => prev.filter((m) => m !== startMarker));
        }
      };

      const cleanup = () => {
        map.off("mousemove", onMouseMove);
        map.off("click", onGeoEnd);
        document.removeEventListener("keydown", onEscape);
        map.getContainer().style.cursor = "";
        stopRubberBand(map);
        awaitingGeoEndRef.current = false;
        setAwaitingEnd(false);
        activeDrawCleanupRef.current = null;
      };

      activeDrawCleanupRef.current = cleanup;
      map.on("mousemove", onMouseMove);
      map.once("click", onGeoEnd);
      document.addEventListener("keydown", onEscape);
    });
  }

  // ---------------------------------------------------------------------------
  // HandleDrawClickLocation  (Georeference by Location mode)
  // ---------------------------------------------------------------------------
  function HandleDrawClickLocation() {
    map.getContainer().style.cursor = "crosshair";

    map.once("click", function onCreated(e) {
      map.getContainer().style.cursor = "";
      const markerLL = e.latlng;

      if (!bounds.current || !bounds.current.contains(markerLL)) {
        alert("The starting point must be inside the image.");
        return;
      }

      const startMarker = L.marker(markerLL, {
        icon: new customMarker({ type: "geo_start" }),
        draggable: false,
        interactive: false,
      }).addTo(map);

      setMarkers((prev) => [...prev, startMarker]);
      setInitialMarkerLatLng(markerLL);
      setInputLat(markerLL.lat);
      setInputLng(markerLL.lng);
      setInputLatLngModal(true);
    });
  }

  // ---------------------------------------------------------------------------
  // saveLatLng
  // ---------------------------------------------------------------------------
  const saveLatLng = () => {
    if (!inputLat || !inputLng) {
      alert("Latitude and Longitude cannot be empty.");
      return;
    }
    if (!initialMarkerLatLng) {
      console.warn("Initial marker LatLng not set.");
      return;
    }

    const newLatLng = new L.LatLng(parseFloat(inputLat), parseFloat(inputLng));
    const pixelCoords = latLngToImagePixel(initialMarkerLatLng);
    const lineId = Math.random().toString(36).substring(2, 15);

    drawLine(initialMarkerLatLng, newLatLng, pixelCoords, lineId, true);
    setLineCount((prev) => prev + 1);
    setInputLat("");
    setInputLng("");
    setInputLatLngModal(false);
  };

  // ---------------------------------------------------------------------------
  // Drawing helpers
  // ---------------------------------------------------------------------------
  const drawLine = (ll1, ll2, pixelCoords, id, transparent = false) => {
    if (!mapRef) return;
    const polyline = L.polyline([ll1, ll2], {
      color: transparent ? "transparent" : "red",
      weight: 2,
    }).addTo(mapRef);
    polylines.current = { ...polylines.current, [id]: polyline };
    setLines((prev) => [...prev, { pixelCoords, latLngs: ll2 }]);
  };

  const updateLine = (ll1, newLL, id) => {
    if (!mapRef) return;
    const existing = polylines.current[id];
    if (existing) {
      existing.remove();
      const { [id]: _, ...rest } = polylines.current;
      polylines.current = rest;
    }
    const updated = L.polyline([ll1, newLL], { color: "red", weight: 2 }).addTo(mapRef);
    polylines.current = { ...polylines.current, [id]: updated };
  };

  const removeAllLines = () => {
    if (activeDrawCleanupRef.current) activeDrawCleanupRef.current();
    Object.values(polylines.current).forEach((p) => p.remove());
    markers.forEach((m) => { try { m.remove(); } catch {} });
    stopRubberBand(map || mapRef);
    awaitingGeoEndRef.current = false;
    setAwaitingEnd(false);
    if (map) map.getContainer().style.cursor = "";
    setMarkers([]);
    polylines.current = {};
    setLines([]);
    setLineCount(0);
  };

  const latLngToImagePixel = (ll) => {
    const tl = bounds.current.getNorthWest();
    const br = bounds.current.getSouthEast();
    const latR = (tl.lat - ll.lat) / (tl.lat - br.lat);
    const lngR = (ll.lng - tl.lng) / (br.lng - tl.lng);
    return {
      x: Math.round(lngR * (width.current || 1)),
      y: Math.round(latR * (height.current || 1)),
    };
  };

  // ---------------------------------------------------------------------------
  // Modify toggle
  // ---------------------------------------------------------------------------
  function HandleModify() {
    markers.forEach((marker) => {
      if (marker.options?.icon?.options?.type === "geo_end") {
        modify ? marker.dragging.disable() : marker.dragging.enable();
      }
    });
    setModify((prev) => !prev);
  }

  // ---------------------------------------------------------------------------
  // GeoRef
  // ---------------------------------------------------------------------------
  async function GeoRef() {
    if (!geoRefMode) {
      alert("Please select Georeference by Marker or Georeference by Location.");
      return;
    }
    if (lineCount < 3) {
      alert(
        `At least 3 reference lines are required for an accurate georeference.\n` +
          `You have drawn ${lineCount} line${lineCount === 1 ? "" : "s"} so far. ` +
          `Please draw ${3 - lineCount} more.`
      );
      return;
    }

    const res = await fetch(`${HOST}/georef-image`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ lines, path: path.current }),
    });

    if (res.ok) {
      const result = await res.json();
      bounds.current = L.latLngBounds(
        L.latLng(result.new_bounds[1], result.new_bounds[0]),
        L.latLng(result.new_bounds[3], result.new_bounds[2])
      );
      addImageOverlay(path.current, false, false, true);
      setDownload(true);
      removeAllLines();
    }
  }

  // ---------------------------------------------------------------------------
  // Download
  // ---------------------------------------------------------------------------
  async function DownloadGeoRef() {
    try {
      const res = await fetch(`${HOST}/download-georef-image`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": await getCsrfToken(),
        },
        body: JSON.stringify({ path: path.current }),
      });

      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const filename = path.current
          .split("/")
          .pop()
          .replace(/\.[^.]+$/, ".tif");
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        console.error("Failed to download GeoTIFF:", res.statusText);
      }
    } catch (error) {
      console.error("Error downloading TIFF:", error);
    }
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div
      style={{ position: show ? "absolute" : "relative" }}
      className="toolscont"
    >
      {/* ── Trigger Button ── */}
      <button
        title="Geo Tool"
        className="btn text-white"
        id="roadroute"
        onClick={() => setShow((prev) => !prev)}
        style={{
          backgroundColor: "black",
          width: "40px",
          height: "40px",
          borderRadius: "50%",
          border: "none",
        }}
      >
        <i className="fa-solid fa-book-atlas" />
      </button>

      {show && (
        <div
          ref={toolvisRef}
          className="toolsvis"
          style={{ display: "flex", flexDirection: "column", width: "220px", padding: "10px 12px" }}
        >
          {/* ── Header ── */}
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingBottom: "8px",
            marginBottom: "10px",
            borderBottom: "1px solid rgba(255,255,255,0.12)",
          }}>
            <span style={{ color: "#ccc", fontSize: "13px", fontWeight: 600 }}>
              <i className="fa-solid fa-book-atlas" style={{ marginRight: "6px", fontSize: "11px" }} />
              Geo Reference
            </span>
            <span
              onClick={() => setShow(false)}
              style={{ color: "#e74c3c", fontSize: "20px", cursor: "pointer", lineHeight: 1 }}
            >
              &times;
            </span>
          </div>

          {/* ── Body ── */}
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>

            {/* File upload section */}
            <div className="toolsvis-body">
              <input
                className="form-control"
                style={{ width: "100%", fontSize: "11px", color: "black" }}
                type="file"
                accept=".jpg,.png,.jpeg"
                onChange={handleFileChange}
                multiple
                ref={fileInputRef}
              />
            </div>

            <button   style={{ width: "100%" }} onClick={handleUpload}>
              Upload Image
            </button>

            {uploadProgress > 0 && uploadProgress < 100 && (
              <small style={{ color: "#d3d3d3", fontSize: "11px" }}>
                Uploading: {uploadProgress}%
              </small>
            )}

            <small style={{ color: "rgba(255,255,255,0.4)", fontSize: "10px" }}>
              Accepted: .jpg, .png, .jpeg
            </small>

            {/* ── Mode selection ── */}
            <div  style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <label style={{ color: "#ccc", fontSize: "12px", display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
                <input
                  type="radio"
                  name="georef-mode"
                  checked={geoRefMode === "marker"}
                  onChange={() => handleGeoRefModeChange("marker")}
                />
                Georeference by Marker
              </label>
              <label style={{ color: "#ccc", fontSize: "12px", display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
                <input
                  type="radio"
                  name="georef-mode"
                  checked={geoRefMode === "location"}
                  onChange={() => handleGeoRefModeChange("location")}
                />
                Georeference by Location
              </label>
            </div>

            {/* ── Line counter ── */}
            {geoRefMode && (
              <small style={{
                color: lineCount >= 3 ? "#90ee90" : "#ffd700",
                fontSize: "11px",
              }}>
                {lineCount < 3
                  ? `Lines drawn: ${lineCount} / 3 minimum required`
                  : `Lines drawn: ${lineCount}`}
              </small>
            )}

            {/* ── Status hint while awaiting second point ── */}
            {awaitingEnd ? (
              <div style={{
                padding: "6px 8px",
                backgroundColor: "rgba(255,255,255,0.06)",
                borderRadius: "4px",
                border: "1px solid rgba(255,255,255,0.1)",
              }}>
                <small style={{ color: "#add8e6", fontSize: "11px", display: "block" }}>
                  📍 Click the real-world location on the map
                </small>
                <small style={{ color: "rgba(255,255,255,0.35)", fontSize: "10px" }}>
                  Press Esc to cancel
                </small>
              </div>
            ) : (
              geoRefMode === "marker" && (
                <small style={{ color: "rgba(255,255,255,0.45)", fontSize: "11px" }}>
                  Click a point on the image, then its real location on the map.
                </small>
              )
            )}

            {/* ── Draw button ── */}
            {geoRefMode && (
              <button
              
                style={{
                  width: "100%",
                  opacity: awaitingEnd ? 0.45 : 1,
                  cursor: awaitingEnd ? "not-allowed" : "pointer",
                }}
                disabled={awaitingEnd}
                onClick={() => {
                  if (geoRefMode === "marker") HandleDrawClick();
                  else HandleDrawClickLocation();
                }}
              >
                {geoRefMode === "marker" ? "Draw Line" : "Draw Points"}
              </button>
            )}

            {/* ── Modify Line ── */}
            {GeoRefByLine && lines.length > 0 && (
              <button
                 
                style={{ width: "100%", backgroundColor: modify ? "rgba(255,255,255,0.15)" : "" }}
                onClick={HandleModify}
              >
                {modify ? "Stop Modifying" : "Modify Line"}
              </button>
            )}

            {/* ── Clear All ── */}
            {lineCount > 0 && (
              <button
                 
                style={{ width: "100%", backgroundColor: "rgba(180,30,30,0.55)" }}
                onClick={removeAllLines}
              >
                Clear All
              </button>
            )}

            {/* ── Geo ── */}
            <button   style={{ width: "100%" }} onClick={GeoRef}>
              Geo
            </button>

            {/* ── Download ── */}
            {download && (
              <button   style={{ width: "100%" }} onClick={DownloadGeoRef}>
                Download
              </button>
            )}

            {/* ── Error ── */}
            {mess && (
              <small style={{ color: "#ff6b6b", fontSize: "11px" }}>{mess}</small>
            )}
          </div>
        </div>
      )}

      {/* ── Lat/Lng modal (Location mode) ── */}
      {inputLatLngModal && (
        <Modal
          isOpen={inputLatLngModal}
          onRequestClose={() => setInputLatLngModal(false)}
          className="col-lg-2 col-sm-2 custom-modal"
        >
          <div className="user-modal-header">
            <i
              className="fa-solid fa-xmark cancel"
              onClick={() => setInputLatLngModal(false)}
            />
          </div>

          <div className="col-lg-12 col-md-6 col-sm-10">
            Latitude:
            <input
              type="text"
              className="form-control"
              style={{ width: "100%", fontSize: "11px", color: "black" }}
              value={inputLat}
              onChange={(e) => setInputLat(e.target.value)}
            />
            Longitude:
            <input
              type="text"
              className="form-control"
              style={{ width: "100%", fontSize: "11px", color: "black" }}
              value={inputLng}
              onChange={(e) => setInputLng(e.target.value)}
            />
          </div>

          <div className="modal-footer">
            <button onClick={saveLatLng} className="mt-3 btn-add">
              Save
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default GeoReference;